import { auth } from './firebase'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

interface RequestOptions extends RequestInit {
  params?: Record<string, string>
}

async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken()
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...init } = options

  let url = `${API_BASE}${path}`
  if (params) {
    const searchParams = new URLSearchParams(params)
    url += `?${searchParams.toString()}`
  }

  const token = await getAuthToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...init,
    headers,
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || `Request failed: ${response.status}`)
  }

  // Handle 204 No Content (e.g., DELETE responses)
  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// Workers API
export const workersApi = {
  list: (params?: { status?: string; poolId?: string }) =>
    request<{ workers: Worker[]; total: number }>('/api/workers', { params }),

  get: (id: string) =>
    request<Worker>(`/api/workers/${id}`),
}

// Pools API
export const poolsApi = {
  list: () =>
    request<{ pools: PoolWithMetrics[] }>('/api/pools'),

  get: (id: string) =>
    request<PoolWithMetrics>(`/api/pools/${id}`),

  create: (pool: CreatePoolRequest) =>
    request<Pool>('/api/pools', { method: 'POST', body: JSON.stringify(pool) }),

  update: (id: string, update: UpdatePoolRequest) =>
    request<Pool>(`/api/pools/${id}`, { method: 'PATCH', body: JSON.stringify(update) }),

  delete: (id: string) =>
    request<void>(`/api/pools/${id}`, { method: 'DELETE' }),

  scale: (id: string, scale: ScalePoolRequest) =>
    request<ScalePoolResponse>(`/api/pools/${id}/scale`, { method: 'POST', body: JSON.stringify(scale) }),

  // AutoPilot
  getAutoPilot: (id: string) =>
    request<AutoPilotConfig>(`/api/pools/${id}/autopilot`),

  updateAutoPilot: (id: string, config: Partial<AutoPilotConfig>) =>
    request<AutoPilotConfig>(`/api/pools/${id}/autopilot`, { method: 'POST', body: JSON.stringify(config) }),

  getAutoPilotStatus: (id: string) =>
    request<AutoPilotStatus>(`/api/pools/${id}/autopilot/status`),
}

// Queue API
export const queueApi = {
  getStats: () =>
    request<QueueStats>('/api/queue/stats'),

  listJobs: (params?: { status?: string; limit?: number; offset?: number }) =>
    request<{ jobs: QueueJob[]; total: number }>('/api/queue/jobs', { params: params as Record<string, string> }),
}

// ETA API
export const etaApi = {
  get: (serviceLevel: 'BASIC' | 'FULL') =>
    request<ETAResult>(`/api/eta/${serviceLevel}`),
}

// Types
export interface Worker {
  id: string
  poolId: string
  status: 'idle' | 'processing' | 'offline'
  location: string
  concurrency: number
  currentJob?: JobInfo
  jobsTotal: number
  jobsErrors: number
  lastHeartbeat: string
  startedAt: string
}

export interface JobInfo {
  jobId: string
  reviewId: string
  status: string
  progress: string
  startedAt: string
  apiKeyIndex?: number
  videoResolution?: string
  videoBitrateKbps?: number
  fileSizeMB?: number
}

export interface AutoPilotConfig {
  enabled: boolean
  minInstances: number
  maxInstances: number
  minConcurrency: number
  maxConcurrency: number
  cooldownSeconds?: number
}

export interface AutoPilotStatus {
  enabled: boolean
  currentAction: null | {
    type: string
    reason: string
  }
  lastAction: null | {
    type: string
    timestamp: string
    reason: string
  }
  nextEvaluation: string
  currentMetrics: {
    pendingJobs: number
    processingJobs: number
    utilization: number
    queuePressure: number
  }
}

export interface Pool {
  id: string
  name: string
  provider: 'cloudrun' | 'flyio' | 'onprem'
  region: string
  cloudRunService?: string
  flyAppName?: string
  targetConcurrency: number
  minInstances: number
  maxInstances: number
  enabled: boolean
  autoPilotConfig?: AutoPilotConfig
  createdAt: string
  updatedAt: string
}

export interface PoolWithMetrics extends Pool {
  currentInstances: number
  activeWorkers: number
  idleWorkers: number
  offlineWorkers: number
  jobsProcessed24h: number
  avgProcessingTimeMs: number
}

export interface CreatePoolRequest {
  id: string
  name: string
  provider: 'cloudrun' | 'flyio' | 'onprem'
  region?: string
  cloudRunService?: string
  flyAppName?: string
  targetConcurrency?: number
  minInstances?: number
  maxInstances?: number
}

export interface UpdatePoolRequest {
  name?: string
  targetConcurrency?: number
  minInstances?: number
  maxInstances?: number
  enabled?: boolean
}

export interface ScalePoolRequest {
  minInstances?: number
  maxInstances?: number
}

export interface ScalePoolResponse {
  poolId: string
  previousMin: number
  previousMax: number
  newMin: number
  newMax: number
  status: 'scaling' | 'completed' | 'failed'
  message: string
}

export interface QueueStats {
  pendingJobs: number
  processingJobs: number
  completedLast24h: number
  failedLast24h: number
  avgWaitTimeMs: number
  avgProcessingTimeMs: number
  throughputPerHour: number
}

export interface QueueJob {
  jobId: string
  reviewId: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  serviceLevel: 'BASIC' | 'FULL'
  videoSource: 'youtube' | 'upload'
  workerId?: string
  queuedAt: string
  startedAt?: string
  completedAt?: string
}

export interface ETAResult {
  estimatedSeconds: number
  estimatedComplete: string
  confidence: 'high' | 'medium' | 'low'
  queuePosition: number
  activeWorkers: number
  breakdown: {
    queueDelaySeconds: number
    processingSeconds: number
    basedOn: 'historical' | 'heuristic'
  }
  calculatedAt: string
}
